import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  LinearProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Folder as FolderIcon,
  Router as RouterIcon,
  Notifications as NotificationsIcon,
  VpnKey as VpnKeyIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { API } from '../services/api';
import KPICard from '../components/KPICard';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    companies: 0,
    projects: 0,
    devices: 0,
    alerts: 0,
    apiKeys: 0,
    jobs: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const [
          companiesRes,
          projectsRes,
          devicesRes,
          alertsRes,
          apiKeysRes,
          jobsRes,
        ] = await Promise.allSettled([
          API.companies.list(),
          API.projects.list(),
          API.iotDevices.list(),
          API.alertRules.list(),
          API.apiKeys.list(),
          API.scheduler.jobs.list(),
        ]);

        // Extract counts from response data
        const getCount = (res) => {
          if (res.status === 'fulfilled') {
            const data = res.value?.data;
            if (Array.isArray(data)) {
              return data.length;
            }
            return res.value?.total || 0;
          }
          return 0;
        };

        setStats({
          companies: getCount(companiesRes),
          projects: getCount(projectsRes),
          devices: getCount(devicesRes),
          alerts: getCount(alertsRes),
          apiKeys: getCount(apiKeysRes),
          jobs: getCount(jobsRes),
        });
      } catch (err) {
        console.error('Dashboard error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return (
      <Box>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 3 }}>
          Dashboard Overview
        </Typography>
        <LinearProgress sx={{ mb: 3, borderRadius: 1 }} />
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Paper sx={{ height: 130, borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Box mb={3}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
          Dashboard Overview
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Welcome back! Here's what's happening with your system today.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Companies"
            value={stats.companies}
            icon={<BusinessIcon />}
            color="#6366F1"
            delay={0}
            onClick={() => navigate('/companies')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Projects"
            value={stats.projects}
            icon={<FolderIcon />}
            color="#10B981"
            delay={0.1}
            onClick={() => navigate('/projects')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="IoT Devices"
            value={stats.devices}
            icon={<RouterIcon />}
            color="#F59E0B"
            delay={0.2}
            onClick={() => navigate('/iot-devices')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Active Alerts"
            value={stats.alerts}
            icon={<NotificationsIcon />}
            color="#EF4444"
            delay={0.3}
            onClick={() => navigate('/alerts')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="API Keys"
            value={stats.apiKeys}
            icon={<VpnKeyIcon />}
            color="#EC4899"
            delay={0.4}
            onClick={() => navigate('/api-keys')}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Scheduled Jobs"
            value={stats.jobs}
            icon={<ScheduleIcon />}
            color="#8B5CF6"
            delay={0.5}
            onClick={() => navigate('/scheduled-jobs')}
          />
        </Grid>
      </Grid>

      <Box sx={{ mt: 5 }}>
        <Typography variant="h6" component="h2" sx={{ mb: 3, fontWeight: 700 }}>
          Quick Insights
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <MotionCard
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.6 }}
              sx={{ borderRadius: 3, height: '100%' }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Box 
                    sx={{ 
                      backgroundColor: 'success.lighter', 
                      borderRadius: 2, 
                      p: 1.5,
                      mr: 2,
                    }}
                  >
                    <CheckCircleIcon sx={{ color: 'success.main', fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      System Status
                    </Typography>
                    <Typography variant="body2" color="success.main" fontWeight={600}>
                      All systems operational
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Identity service is running smoothly. All endpoints are responding normally.
                </Typography>
              </CardContent>
            </MotionCard>
          </Grid>

          <Grid item xs={12} md={6}>
            <MotionCard
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, delay: 0.7 }}
              sx={{ borderRadius: 3, height: '100%' }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Box 
                    sx={{ 
                      backgroundColor: 'primary.lighter', 
                      borderRadius: 2, 
                      p: 1.5,
                      mr: 2,
                    }}
                  >
                    <TrendingUpIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      Performance
                    </Typography>
                    <Typography variant="body2" color="primary.main" fontWeight={600}>
                      Excellent
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  System performance is optimal. Response times are within expected ranges.
                </Typography>
              </CardContent>
            </MotionCard>
          </Grid>
        </Grid>
      </Box>
    </MotionBox>
  );
}

export default Dashboard;